import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";


function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

function SidebarLink({ to, label, end = false, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        classNames(
          "group relative flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
          isActive
            ? "bg-white text-[#050713] shadow-[0_16px_40px_rgba(0,0,0,0.28)]"
            : "text-slate-400 hover:bg-white/[0.07] hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={classNames(
              "mr-3 h-1.5 w-1.5 rounded-full transition",
              isActive
                ? "bg-[#C59B45]"
                : "bg-slate-700 group-hover:bg-[#C59B45]",
            )}
          />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function SidebarSection({ title, children }) {
  return (
    <div>
      <p className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500">
        {title}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function SidebarContent({ showAdminLink = false, onNavigate }) {
  const [wallet, setWallet] = useState(() =>
    String(localStorage.getItem("chainforge_wallet_address") || "")
      .trim()
      .toLowerCase(),
  );

  useEffect(() => {
    const syncWallet = () => {
      setWallet(
        String(localStorage.getItem("chainforge_wallet_address") || "")
          .trim()
          .toLowerCase(),
      );
    };

    syncWallet();

    window.addEventListener("focus", syncWallet);
    window.addEventListener("storage", syncWallet);
    window.addEventListener("chainforge:wallet-changed", syncWallet);

    return () => {
      window.removeEventListener("focus", syncWallet);
      window.removeEventListener("storage", syncWallet);
      window.removeEventListener("chainforge:wallet-changed", syncWallet);
    };
  }, []);

  let historyLink = "/history";

  if (wallet) {
    const storageKey = `chainforge_active_history_token_${wallet}`;

    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.network && parsed?.address) {
          historyLink = `/token/${parsed.network}/${parsed.address}?source=history`;
        }
      }
    } catch {
      // ignore storage errors
    }
  }

  return (
    <>
      <div className="border-b border-white/10 px-4 py-4">
        <a href="/studio" className="flex items-center gap-3">
          <img
            src="/logos/krionex-icon.png"
            alt="Krionex"
            className="h-10 w-10 shrink-0 rounded-xl object-contain shadow-[0_14px_35px_rgba(0,0,0,0.28)]"
          />

          <div className="min-w-0">
            <div className="truncate text-sm font-semibold tracking-[0.26em] text-[#E6C978]">
              KRIONEX
            </div>
            <div className="mt-1 truncate text-[9px] text-slate-500">
              The Operating Layer For Onchain Products
            </div>
          </div>
        </a>

        <div className="mt-3 rounded-2xl border border-[#C59B45]/15 bg-black/20 px-3 py-2">
          <div className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#E6C978]">
            Workspace
          </div>
          <div className="mt-1 text-[11px] leading-5 text-slate-400">
            Deploy, operate, and manage contracts.
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="space-y-6">
          <SidebarSection title="Build">
            <SidebarLink to="/studio" label="Studio" onClick={onNavigate} />
            <SidebarLink to="/operations" label="Operations" onClick={onNavigate} />
            <SidebarLink to="/deployments" label="Deployments" onClick={onNavigate} />
            <SidebarLink to={historyLink} label="History" onClick={onNavigate} />
          </SidebarSection>

          <SidebarSection title="Workspace">
            <SidebarLink to="/upgrade" label="Upgrade" onClick={onNavigate} />
            <SidebarLink to="/account" label="Account" onClick={onNavigate} />
            <SidebarLink to="/help-center" label="Support Center" onClick={onNavigate} />
          </SidebarSection>

          <SidebarSection title="Learn">
            <SidebarLink to="/guide" label="User Guide" onClick={onNavigate} />
            <SidebarLink to="/examples" label="Examples" onClick={onNavigate} />
            <SidebarLink to="/about" label="About" onClick={onNavigate} />
            <SidebarLink to="/trust" label="Trust & Security" onClick={onNavigate} />
          </SidebarSection>

          <SidebarSection title="Legal">
            <SidebarLink to="/terms" label="Terms" onClick={onNavigate} />
            <SidebarLink to="/privacy" label="Privacy" onClick={onNavigate} />
            <SidebarLink to="/refund-policy" label="Refund Policy" onClick={onNavigate} />
            <SidebarLink to="/contact" label="Contact" onClick={onNavigate} />
          </SidebarSection>

          {showAdminLink && (
            <SidebarSection title="Administration">
              <SidebarLink to="/admin" label="Admin Dashboard" onClick={onNavigate} />
            </SidebarSection>
          )}
        </div>
      </div>

      <div className="border-t border-white/10 p-2.5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-2.5 shadow-xl shadow-black/15">
          <div className="flex items-center gap-2.5">
            <img
              src="/logos/krionex-icon.png"
              alt="Krionex"
              className="h-8 w-8 shrink-0 rounded-xl object-contain"
            />
            <div className="min-w-0">
              <div className="truncate text-xs font-semibold text-white">
                Infrastructure workspace
              </div>
              <div className="truncate text-[11px] text-slate-500">
                Multi-chain contract operations
              </div>
            </div>
          </div>

          <p className="mt-2 text-[11px] leading-4 text-slate-400">
            Deploy, verify, and manage contracts across supported EVM networks.
          </p>
        </div>
      </div>
    </>
  );
}

export default function Sidebar({
  showAdminLink = false,
  mobileOpen = false,
  onMobileClose,
}) {
  return (
    <>
      <aside className="hidden h-screen w-64 shrink-0 overflow-hidden border-r border-white/10 bg-[#050713] lg:flex lg:flex-col">
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-64 bg-[radial-gradient(circle_at_top_left,rgba(197,155,69,0.14),transparent_30%),radial-gradient(circle_at_bottom,rgba(79,70,229,0.09),transparent_34%)] lg:block" />
        <div className="relative flex h-full min-h-0 flex-col">
          <SidebarContent showAdminLink={showAdminLink} />
        </div>
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation overlay"
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
            onClick={onMobileClose}
          />

          <aside className="relative flex h-full w-[88vw] max-w-[360px] flex-col overflow-hidden border-r border-white/10 bg-[#050713] shadow-2xl">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(197,155,69,0.14),transparent_34%),radial-gradient(circle_at_bottom,rgba(79,70,229,0.08),transparent_36%)]" />

            <div className="absolute right-4 top-4 z-10">
              <button
                type="button"
                onClick={onMobileClose}
                className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-xl text-white shadow-lg backdrop-blur transition hover:bg-white/15"
                aria-label="Close navigation"
              >
                ×
              </button>
            </div>

            <div className="relative flex h-full min-h-0 flex-col">
              <SidebarContent
                showAdminLink={showAdminLink}
                onNavigate={onMobileClose}
              />
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
}